/**
 * custom-email-preferences.js
 * Email preference centre — sections/custom-email-preferences.liquid.
 *
 * Two jobs:
 *   1. Keep the "Send me everything" master checkbox in lockstep with the five
 *      category checkboxes, instantly and with no network round trip.
 *   2. Persist on Save through the app proxy at /apps/email-preferences, which
 *      is where the identity check happens. Nothing here sends a customer id —
 *      Shopify signs `logged_in_customer_id` into the proxied request, so the
 *      page has no say in whose preferences get written.
 *
 * Deliberately plain: loaded with `defer` by the section, no importmap and no
 * dependencies. The markup is fully server-rendered, so this file only ever
 * enhances state that is already correct on screen.
 *
 * phaser: adapt — logic is portable, the five keys are CCC categories.
 */

(function () {
  'use strict';

  var PROXY_URL = '/apps/email-preferences';
  var SAVE_TIMEOUT_MS = 15000;

  var ICON_SUCCESS =
    '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">' +
    '<path d="M4.5 10.5 8 14l7.5-7.5" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_ERROR =
    '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">' +
    '<circle cx="10" cy="10" r="7.5" fill="none" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M10 6v5m0 2.6v.4" fill="none" stroke="currentColor" stroke-width="1.8" ' +
    'stroke-linecap="round"/></svg>';

  function init(form) {
    var items = Array.prototype.slice.call(form.querySelectorAll('[data-email-pref]'));
    var saveButton = form.querySelector('[data-email-prefs-save]');
    var status = form.querySelector('[data-email-prefs-status]');
    var interval = form.querySelector('[data-email-prefs-interval]');
    // The roast row owns the cadence control.
    var roastInput = form.querySelector('[data-email-pref="friday_roast_reminders"]');
    // The unsubscribe card sits OUTSIDE the form (below the categories), so
    // it is found at section scope, not form scope.
    var section = form.closest('.email-prefs') || document;
    var unsubOpen = section.querySelector('[data-email-prefs-unsub-open]');
    var unsubConfirm = section.querySelector('[data-email-prefs-unsub-confirm]');
    var unsubYes = section.querySelector('[data-email-prefs-unsub-yes]');
    var unsubCancel = section.querySelector('[data-email-prefs-unsub-cancel]');
    var resubButton = section.querySelector('[data-email-prefs-resub]');
    if (!items.length || !saveButton) return;

    // Server-rendered state is the baseline: Save stays disabled until the
    // customer actually changes something.
    var saved = snapshot();
    var savedInterval = interval ? interval.value : null;
    var inFlight = false;

    function snapshot() {
      return items.reduce(function (acc, input) {
        acc[input.getAttribute('data-email-pref')] = input.checked;
        return acc;
      }, {});
    }

    function isDirty() {
      var current = snapshot();
      var changed = items.some(function (input) {
        var key = input.getAttribute('data-email-pref');
        return current[key] !== saved[key];
      });
      // A cadence change on its own is worth saving.
      if (!changed && interval && interval.value !== savedInterval) return true;
      return changed;
    }

    /** Cadence only means anything while roast reminders are on. */
    function syncIntervalEnabled() {
      if (!interval || !roastInput) return;
      interval.disabled = !roastInput.checked;
    }

    function refreshSaveState() {
      saveButton.disabled = inFlight || !isDirty();
    }

    /** kind: 'success' | 'error' | null (null = neutral, no icon, no colour). */
    function setStatus(message, kind) {
      if (!status) return;
      status.classList.remove('email-prefs__status--success', 'email-prefs__status--error');
      if (!message) {
        status.textContent = '';
        return;
      }
      if (!kind) {
        status.textContent = message;
        return;
      }
      status.classList.add('email-prefs__status--' + kind);
      status.innerHTML = (kind === 'success' ? ICON_SUCCESS : ICON_ERROR) + '<span></span>';
      // Message goes in via textContent so a server string can never inject markup.
      status.querySelector('span').textContent = message;
    }

    items.forEach(function (input) {
      input.addEventListener('change', function () {
        syncIntervalEnabled();
        setStatus('', null);
        refreshSaveState();
      });
    });

    if (interval) {
      interval.addEventListener('change', function () {
        setStatus('', null);
        refreshSaveState();
      });
    }

    // Reconcile once on load in case a bfcache restore or browser form
    // restoration reinstated checkbox state after the server render.
    syncIntervalEnabled();
    refreshSaveState();

    // Full unsubscribe: two-step confirm, then a one-way consent revocation
    // through the same authenticated proxy. On success the page reloads so the
    // server renders the unsubscribed notice - the truthful source of state.
    if (unsubOpen && unsubConfirm && unsubYes && unsubCancel) {
      unsubOpen.addEventListener('click', function () {
        unsubOpen.hidden = true;
        unsubConfirm.hidden = false;
        unsubYes.focus();
      });
      unsubCancel.addEventListener('click', function () {
        unsubConfirm.hidden = true;
        unsubOpen.hidden = false;
        unsubOpen.focus();
      });
      unsubYes.addEventListener('click', function () {
        if (inFlight) return;
        inFlight = true;
        unsubYes.disabled = true;
        unsubCancel.disabled = true;
        setStatus('Unsubscribing...', null);

        fetch(PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ unsubscribe: true }),
        })
          .then(function (response) {
            return response
              .json()
              .catch(function () {
                return null;
              })
              .then(function (body) {
                if (!response.ok || !body || body.ok !== true) {
                  throw new Error((body && body.error) || 'We could not unsubscribe you. Please try again.');
                }
                // Reload so the server-rendered unsubscribed notice takes over.
                window.location.reload();
              });
          })
          .catch(function (error) {
            inFlight = false;
            unsubYes.disabled = false;
            unsubCancel.disabled = false;
            setStatus((error && error.message) || 'We could not unsubscribe you. Please try again.', 'error');
          });
      });
    }

    // Resubscribe: the one consent-ON action, fired only by this direct click.
    // No confirm step - opting back IN is low-harm and instantly reversible.
    // On success the page reloads so the server-rendered state takes over.
    if (resubButton) {
      resubButton.addEventListener('click', function () {
        if (inFlight) return;
        inFlight = true;
        resubButton.disabled = true;
        setStatus('Resubscribing...', null);

        fetch(PROXY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ resubscribe: true }),
        })
          .then(function (response) {
            return response
              .json()
              .catch(function () {
                return null;
              })
              .then(function (body) {
                if (!response.ok || !body || body.ok !== true) {
                  throw new Error((body && body.error) || 'We could not resubscribe you. Please try again.');
                }
                window.location.reload();
              });
          })
          .catch(function (error) {
            inFlight = false;
            resubButton.disabled = false;
            setStatus((error && error.message) || 'We could not resubscribe you. Please try again.', 'error');
          });
      });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (inFlight || saveButton.disabled) return;

      var payload = snapshot();
      var intervalValue = interval ? Number(interval.value) : null;
      inFlight = true;
      saveButton.disabled = true;
      setStatus('Saving...', null);

      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () {
        if (controller) controller.abort();
      }, SAVE_TIMEOUT_MS);

      fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // Storefront session cookie is what lets Shopify resolve
        // logged_in_customer_id before forwarding to the worker.
        credentials: 'same-origin',
        body: JSON.stringify({
          preferences: payload,
          roast_reminder_weeks: intervalValue,
        }),
        signal: controller ? controller.signal : undefined,
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return null;
            })
            .then(function (body) {
              // Never treat a non-2xx (or an unparseable body) as saved.
              if (!response.ok || !body || body.ok !== true) {
                var reason = body && body.error ? body.error : null;
                throw new Error(reason || httpMessage(response.status));
              }
              return body;
            });
        })
        .then(function (body) {
          // Trust the server's echo over the local guess, so the baseline can
          // never drift from what was actually written.
          if (body.preferences) {
            items.forEach(function (input) {
              var key = input.getAttribute('data-email-pref');
              if (typeof body.preferences[key] === 'boolean') {
                input.checked = body.preferences[key];
              }
            });
            syncIntervalEnabled();
          }
          if (interval && typeof body.intervalWeeks === 'number') {
            interval.value = String(body.intervalWeeks);
          }
          saved = snapshot();
          savedInterval = interval ? interval.value : null;
          setStatus('Email preferences updated', 'success');
        })
        .catch(function (error) {
          setStatus(errorMessage(error), 'error');
        })
        .then(function () {
          clearTimeout(timer);
          inFlight = false;
          refreshSaveState();
        });
    });

    function httpMessage(statusCode) {
      if (statusCode === 401 || statusCode === 403) {
        return 'Your session has expired. Please log in again and retry.';
      }
      if (statusCode === 429) return 'Too many attempts. Please wait a moment and try again.';
      return 'We could not save your preferences. Please try again.';
    }

    function errorMessage(error) {
      if (error && error.name === 'AbortError') {
        return 'That took too long. Please check your connection and try again.';
      }
      return (error && error.message) || 'We could not save your preferences. Please try again.';
    }
  }

  function boot() {
    var forms = document.querySelectorAll('[data-email-prefs-form]');
    Array.prototype.forEach.call(forms, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
